import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Package, MessageSquare, Image, Mail, ShoppingBag, Plus, Edit, Trash2, Eye, BookOpen, BarChart3, TrendingUp, Users, HelpCircle, Settings, Calendar } from 'lucide-react';
import { getMachines, getUsedMachines, getRentalMachines, getTestimonials, getRealizations, getContacts, getBlogPosts, deleteMachine, deleteUsedMachine, deleteRentalMachine, deleteTestimonial, deleteRealization, deleteBlogPost, updateContactStatus } from '../services/apiService';
import FAQManagement from './FAQManagement';
import SiteSettings from './SiteSettings';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [machines, setMachines] = useState([]);
  const [usedMachines, setUsedMachines] = useState([]);
  const [rentalMachines, setRentalMachines] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [realizations, setRealizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [machinesData, usedData, rentalData, testimonialsData, realizationsData, contactsData, blogData] = await Promise.all([
        getMachines(),
        getUsedMachines(),
        getRentalMachines(),
        getTestimonials(),
        getRealizations(),
        getContacts(),
        getBlogPosts()
      ]);
      
      setMachines(machinesData);
      setUsedMachines(usedData);
      setRentalMachines(rentalData);
      setTestimonials(testimonialsData);
      setRealizations(realizationsData);
      setContacts(contactsData);
      setBlogPosts(blogData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      await updateContactStatus(contactId, status);
      setContacts((prev) => prev.map((c) => (c.id === contactId ? { ...c, status } : c)));
    } catch (error) {
      console.error('Error updating contact status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) return;

    try {
      switch (type) {
        case 'machine':
          await deleteMachine(id);
          break;
        case 'usedMachine':
          await deleteUsedMachine(id);
          break;
        case 'rentalMachine':
          await deleteRentalMachine(id);
          break;
        case 'testimonial':
          await deleteTestimonial(id);
          break;
        case 'realization':
          await deleteRealization(id);
          break;
        case 'blogPost':
          await deleteBlogPost(id);
          break;
        default:
          break;
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const tabs = [
    { id: 'stats', label: 'Statistiques', icon: BarChart3, count: null },
    { id: 'machines', label: 'Machines', icon: Package, count: machines.length },
    { id: 'occasions', label: 'Occasions', icon: ShoppingBag, count: usedMachines.length },
    { id: 'location', label: 'Location', icon: Calendar, count: rentalMachines.length },
    { id: 'testimonials', label: 'Témoignages', icon: MessageSquare, count: testimonials.length },
    { id: 'realizations', label: 'Réalisations', icon: Image, count: realizations.length },
    { id: 'blog', label: 'Blog', icon: BookOpen, count: blogPosts.length },
    { id: 'contacts', label: 'Contacts', icon: Mail, count: contacts.filter(c => c.status === 'new').length },
    { id: 'faq', label: 'FAQ', icon: HelpCircle, count: null },
    { id: 'settings', label: 'Paramètres', icon: Settings, count: null }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                <span className="text-xl font-bold" style={{ color: '#5C3A21' }}>AKM</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Administration</h1>
                <p className="text-sm text-gray-600">Gestion du contenu du site</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                target="_blank"
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <Eye className="inline w-4 h-4 mr-2" />
                Voir le site
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <LogOut className="inline w-4 h-4 mr-2" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-2 mb-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { backgroundColor: '#5C3A21' } : {}}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                {tab.count !== null ? tab.count : ''}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Statistics Dashboard */}
          {activeTab === 'stats' && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>Tableau de bord statistiques</h2>
              
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <Mail className="w-8 h-8 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-900">{contacts.length}</span>
                  </div>
                  <p className="text-sm font-semibold text-blue-700">Demandes de contact</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {contacts.filter(c => c.status === 'new').length} nouvelles
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <Package className="w-8 h-8 text-green-600" />
                    <span className="text-2xl font-bold text-green-900">{machines.length}</span>
                  </div>
                  <p className="text-sm font-semibold text-green-700">Machines actives</p>
                  <p className="text-xs text-green-600 mt-1">
                    {usedMachines.filter(m => m.status === 'available').length} occasions disponibles
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <BookOpen className="w-8 h-8 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-900">{blogPosts.length}</span>
                  </div>
                  <p className="text-sm font-semibold text-purple-700">Articles de blog</p>
                  <p className="text-xs text-purple-600 mt-1">
                    {blogPosts.reduce((sum, post) => sum + (post.views || 0), 0)} vues totales
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl border border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <Image className="w-8 h-8 text-amber-700" />
                    <span className="text-2xl font-bold text-amber-900">{realizations.length}</span>
                  </div>
                  <p className="text-sm font-semibold text-amber-700">Réalisations</p>
                  <p className="text-xs text-amber-600 mt-1">
                    {testimonials.length} témoignages clients
                  </p>
                </div>
              </div>

              {/* Top Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Blog Posts */}
                <div className="border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <TrendingUp className="w-5 h-5 mr-2" style={{ color: '#5C3A21' }} />
                    <h3 className="font-bold text-lg" style={{ color: '#5C3A21' }}>Articles les plus lus</h3>
                  </div>
                  {blogPosts.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucun article publié</p>
                  ) : (
                    <div className="space-y-3">
                      {blogPosts
                        .sort((a, b) => (b.views || 0) - (a.views || 0))
                        .slice(0, 5)
                        .map((post, idx) => (
                          <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                              <div>
                                <p className="font-semibold text-sm">{post.title}</p>
                                <p className="text-xs text-gray-500">{post.views || 0} vues</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Recent Contacts */}
                <div className="border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <Users className="w-5 h-5 mr-2" style={{ color: '#5C3A21' }} />
                    <h3 className="font-bold text-lg" style={{ color: '#5C3A21' }}>Derniers contacts</h3>
                  </div>
                  {contacts.length === 0 ? (
                    <p className="text-gray-500 text-sm">Aucun contact reçu</p>
                  ) : (
                    <div className="space-y-3">
                      {contacts
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 5)
                        .map((contact) => (
                          <div key={contact.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-semibold text-sm">{contact.name}</p>
                              <p className="text-xs text-gray-600">{contact.email}</p>
                              <p className="text-xs text-gray-500 mt-1">{contact.subject}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              contact.status === 'new' ? 'bg-blue-100 text-blue-800' :
                              contact.status === 'read' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {contact.status === 'new' ? 'Nouveau' : contact.status === 'read' ? 'Lu' : 'Répondu'}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Analytics Note */}
              <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <BarChart3 className="w-6 h-6 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-bold text-blue-900 mb-2">Google Analytics 4</h4>
                    <p className="text-sm text-blue-700 mb-2">
                      Pour des statistiques avancées (sources de trafic, taux de conversion, comportement utilisateur), 
                      configurez Google Analytics 4 :
                    </p>
                    <ol className="text-sm text-blue-700 space-y-1 ml-4 list-decimal">
                      <li>Créez un compte GA4 sur <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline font-semibold">analytics.google.com</a></li>
                      <li>Récupérez votre Measurement ID (format: G-XXXXXXXXXX)</li>
                      <li>Ajoutez-le dans le fichier <code className="bg-blue-100 px-2 py-1 rounded">/app/frontend/.env</code></li>
                      <li>Variable: <code className="bg-blue-100 px-2 py-1 rounded">REACT_APP_GA4_MEASUREMENT_ID=G-XXXXXXXXXX</code></li>
                    </ol>
                    <p className="text-xs text-blue-600 mt-3">
                      ✅ Le tracking est déjà intégré dans le code, il suffit d'ajouter votre ID !
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Machines */}
          {activeTab === 'machines' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Machines</h2>
                <button
                  onClick={() => navigate('/admin/machines/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter une machine</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Machine</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Puissance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {machines.map((machine) => (
                      <tr key={machine.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <img src={machine.image} alt={machine.name} className="w-12 h-12 rounded-lg object-cover" />
                            <div>
                              <p className="font-semibold text-gray-900">{machine.name}</p>
                              <p className="text-sm text-gray-500">{machine.workArea}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: '#5C3A21' }}>
                            {machine.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900">{machine.power}</td>
                        <td className="px-6 py-4 text-gray-900">{machine.price}</td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button
                            onClick={() => navigate(`/admin/machines/edit/${machine.slug}`)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit className="w-5 h-5 inline" />
                          </button>
                          <button
                            onClick={() => handleDelete('machine', machine.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-5 h-5 inline" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {machines.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Aucune machine pour le moment</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Occasions */}
          {activeTab === 'occasions' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Machines d'occasion</h2>
                <button
                  onClick={() => navigate('/admin/occasions/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter une occasion</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {usedMachines.map((machine) => (
                  <div key={machine.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <img src={machine.image} alt={machine.name} className="w-16 h-16 rounded-lg object-cover" />
                        <div>
                          <p className="font-semibold text-gray-900">{machine.name}</p>
                          <p className="text-sm text-gray-500">{machine.type} - {machine.power}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        machine.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {machine.status === 'available' ? 'Disponible' : 'Vendue'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold" style={{ color: '#5C3A21' }}>
                        {machine.price || machine.soldPrice}
                      </p>
                      <div className="space-x-2">
                        <button
                          onClick={() => navigate(`/admin/occasions/edit/${machine.id}`)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-5 h-5 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete('usedMachine', machine.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-5 h-5 inline" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {usedMachines.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucune machine d'occasion</p>
                </div>
              )}
            </div>
          )}

          {/* Location */}
          {activeTab === 'location' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Machines en location</h2>
                <button
                  onClick={() => navigate('/admin/location/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                  data-testid="add-rental-btn"
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter une machine en location</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {rentalMachines.map((machine) => (
                  <div key={machine.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow" data-testid={`rental-admin-card-${machine.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <img src={machine.image} alt={machine.name} className="w-16 h-16 rounded-lg object-cover" />
                        <div>
                          <p className="font-semibold text-gray-900">{machine.name}</p>
                          <p className="text-sm text-gray-500">{machine.type} - {machine.power}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        machine.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                      }`}>
                        {machine.status === 'available' ? 'Disponible' : 'Louée'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {machine.availableAtWorkshop && <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Sur place</span>}
                      {machine.availableOnSite && <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Sur site</span>}
                      {machine.shortTerm && <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Court terme</span>}
                      {machine.longTerm && <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">Long terme</span>}
                    </div>
                    <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => navigate(`/admin/location/edit/${machine.id}`)}
                        className="text-blue-600 hover:text-blue-800"
                        data-testid={`edit-rental-${machine.id}`}
                      >
                        <Edit className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete('rentalMachine', machine.id)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`delete-rental-${machine.id}`}
                      >
                        <Trash2 className="w-5 h-5 inline" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {rentalMachines.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucune machine en location</p>
                </div>
              )}
            </div>
          )}

          {/* Testimonials, Realizations, Contacts - Similar structure */}
          {activeTab === 'testimonials' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Témoignages</h2>
                <button
                  onClick={() => navigate('/admin/testimonials/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter</span>
                </button>
              </div>
              <div className="space-y-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold">{t.name}</p>
                        <p className="text-sm text-gray-600">{t.company} - {t.role}</p>
                        <p className="mt-2 text-gray-700">"{t.text}"</p>
                      </div>
                      <div className="space-x-2">
                        <button onClick={() => navigate(`/admin/testimonials/edit/${t.id}`)} className="text-blue-600">
                          <Edit className="w-5 h-5 inline" />
                        </button>
                        <button onClick={() => handleDelete('testimonial', t.id)} className="text-red-600">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'realizations' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Réalisations</h2>
                <button
                  onClick={() => navigate('/admin/realizations/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Ajouter</span>
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {realizations.map((r) => (
                  <div key={r.id} className="border rounded-lg overflow-hidden">
                    <img src={r.image} alt={r.title} className="w-full h-48 object-cover" />
                    <div className="p-4">
                      <p className="font-bold">{r.title}</p>
                      <p className="text-sm text-gray-600">
                        {r.category}
                        {r.realizationType && (
                          <span className="ml-2 px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">
                            {r.realizationType === 'gravure' && 'Gravure'}
                            {r.realizationType === 'decoupe' && 'Découpe'}
                            {r.realizationType === 'gravure_decoupe' && 'Gravure & Découpe'}
                          </span>
                        )}
                      </p>
                      <div className="mt-2 flex items-center space-x-2">
                        <button 
                          onClick={() => navigate(`/admin/realizations/edit/${r.id}`)} 
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="w-4 h-4 inline" /> Modifier
                        </button>
                        <button 
                          onClick={() => handleDelete('realization', r.id)} 
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4 inline" /> Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'blog' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#5C3A21' }}>Articles de blog</h2>
                <button
                  onClick={() => navigate('/admin/blog/new')}
                  className="px-4 py-2 rounded-lg text-white flex items-center space-x-2"
                  style={{ backgroundColor: '#5C3A21' }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Nouvel article</span>
                </button>
              </div>
              <div className="space-y-4">
                {blogPosts.map((post) => (
                  <div key={post.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-bold text-lg" style={{ color: '#5C3A21' }}>
                              {post.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-1">{post.excerpt}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>{new Date(post.createdAt).toLocaleDateString('fr-FR')}</span>
                              <span>{post.views} vues</span>
                              <span className={`px-2 py-1 rounded ${post.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {post.published ? 'Publié' : 'Brouillon'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete('blogPost', post.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {post.tags.slice(0, 5).map(tag => (
                              <span key={tag} className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-800">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {blogPosts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucun article de blog</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contacts' && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>Demandes de contact</h2>
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${c.status === 'new' ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`} data-testid={`contact-card-${c.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold">{c.name}</p>
                        <p className="text-sm text-gray-600">{c.email} {c.phone && `• ${c.phone}`}</p>
                        {c.company && <p className="text-sm text-gray-600">{c.company}</p>}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        c.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        c.status === 'read' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`} data-testid={`contact-status-${c.id}`}>
                        {c.status === 'new' ? 'Nouveau' : c.status === 'read' ? 'Lu' : 'Répondu'}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Objet : {c.subject}</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{c.message}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Reçu le : {new Date(c.createdAt).toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>

                    {/* Actions de statut */}
                    <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-gray-200">
                      <span className="text-xs font-semibold text-gray-600 mr-2">Marquer comme :</span>
                      {c.status !== 'read' && (
                        <button
                          onClick={() => handleUpdateContactStatus(c.id, 'read')}
                          className="px-3 py-1.5 text-xs rounded-lg bg-yellow-100 text-yellow-800 hover:bg-yellow-200 font-semibold"
                          data-testid={`btn-mark-read-${c.id}`}
                        >
                          Lu
                        </button>
                      )}
                      {c.status !== 'replied' && (
                        <button
                          onClick={() => handleUpdateContactStatus(c.id, 'replied')}
                          className="px-3 py-1.5 text-xs rounded-lg bg-green-100 text-green-800 hover:bg-green-200 font-semibold"
                          data-testid={`btn-mark-replied-${c.id}`}
                        >
                          Répondu / Traité
                        </button>
                      )}
                      {c.status !== 'new' && (
                        <button
                          onClick={() => handleUpdateContactStatus(c.id, 'new')}
                          className="px-3 py-1.5 text-xs rounded-lg bg-blue-100 text-blue-800 hover:bg-blue-200 font-semibold"
                          data-testid={`btn-mark-new-${c.id}`}
                        >
                          Remettre en nouveau
                        </button>
                      )}
                      <a
                        href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject)}`}
                        className="ml-auto px-3 py-1.5 text-xs rounded-lg text-white font-semibold hover:opacity-90"
                        style={{ backgroundColor: '#5C3A21' }}
                        data-testid={`btn-reply-${c.id}`}
                      >
                        Répondre par email
                      </a>
                    </div>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Aucune demande de contact</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && <FAQManagement />}

          {/* Settings Tab */}
          {activeTab === 'settings' && <SiteSettings />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
